import type { FunctionalComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { SafeImage } from '@/components/safe-image.tsx';
import { fetchCampaignAudio } from '@/lib/campaign.api.ts';
import { MSG } from '@/lib/constants.ts';

/**
 * 재생 위치를 물어볼 수 있는 것. 유튜브 플레이어와 이 오디오 플레이어가 함께 만족한다.
 * `play.tsx` 는 이 두 가지만 쓰므로, 형식이 달라도 보고 코드는 하나로 둘 수 있다.
 */
export interface MediaPlayer {
  getCurrentTime(): number;
  getDuration(): number;
  /**
   * 재생을 멈춘다.
   *
   * 서버가 이 재생을 끊었을 때(다른 창에서 시작 등) 화면도 멈춰야 한다.
   * 그러지 않으면 적립되지 않는 줄 모르고 끝까지 듣는다.
   */
  pause(): void;
}

export interface AudioCallbacks {
  onReady?: (player: MediaPlayer) => void;
  /** 유튜브와 같은 숫자를 쓴다 — 1 재생, 2 일시정지, 0 종료 */
  onStateChange?: (state: number) => void;
  onError?: (message: string) => void;
}

interface Props {
  campaignSeq?: number | string;
  userSeq?: number;
  sessionid: string | null;
  /** `prepare` 가 준 한 번만 쓰는 티켓. 이게 없으면 음원을 받을 수 없다 */
  ticket?: string | null;
  /** 앨범 이미지 (없으면 음표만) */
  cover?: string | null;
  callbacks?: AudioCallbacks;
}

/**
 * 음악 미션 플레이어.
 *
 * **파일 주소를 쓰지 않는다.** mp3 는 웹으로 열리지 않는 자리에 있고, 서버가
 * 인증된 요청에만 바이트를 준다. 받은 바이트로 Blob URL 을 만들어 `<audio>` 에
 * 물린다 — 주소창에도, 개발자도구 Network 탭의 링크에도 원본 경로가 남지 않는다.
 *
 * ★ 이것은 **DRM 이 아니다.** 재생할 수 있으면 저장도 할 수 있다. 여기서 막는 것은
 *   '주소를 퍼 나르는 것' 까지다. 진짜 DRM(HLS+AES-128, Widevine)이 필요해지면
 *   인코딩 파이프라인부터 따로 만들어야 한다.
 *
 * ★ 브라우저 기본 컨트롤(`controls`)을 쓰지 않는다. 크롬은 거기에 '다운로드'
 *   메뉴를 붙인다. 재생/일시정지만 직접 그린다. 남은 시간과 진행 막대는 부모
 *   화면(`play.tsx` 의 `.reward-box`)이 이미 그리고 있다.
 */
export const AudioPlayer: FunctionalComponent<Props> = ({
  campaignSeq,
  userSeq,
  sessionid,
  ticket,
  cover,
  callbacks,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string>();
  const [failed, setFailed] = useState<string>();
  const [playing, setPlaying] = useState(false);

  // 콜백은 ref 로 들고 간다. 부모가 매번 새 함수를 넘겨도 음원을 다시 받지 않는다.
  const handlers = useRef(callbacks);
  handlers.current = callbacks;

  /**
   * 이미 받아 둔 음원의 표식 — `미션번호:티켓`.
   *
   * 🔴 **티켓은 한 번만 쓸 수 있다.** 그런데 재생 중에 이펙트가 한 번 더 돌면서
   *   같은 티켓으로 음원을 다시 받으려 했고, 서버가 "이미 사용한 요청입니다" 로
   *   거절했다. 그 실패가 `onError` → `finish()` 로 이어져 **재생 중이던 적립이
   *   취소됐다.** (실제 로그: prepare → audio.mp3 → videoplayback ×6 → audio.mp3 실패)
   *
   *   무엇이 이펙트를 다시 돌렸는지와 무관하게, **같은 티켓으로는 두 번 받지
   *   않는다.** 이미 받아 둔 것이 있으면 그대로 쓴다.
   */
  const loadedRef = useRef<string | null>(null);
  /** 만들어 둔 blob 주소. 화면을 떠날 때 놓아 준다 */
  const urlRef = useRef<string | null>(null);

  /*
    🔴 **재생 중에는 blob 주소를 폐기하지 않는다.**

    개발자도구에서 `blob:` 주소를 더블클릭해 내려받는 경로를 막으려고, 다 받은
    시점(`canplaythrough`)에 `URL.revokeObjectURL()` 을 불렀었다. 그랬더니 재생이
    시작되지 않고 화면이 '재생 버튼을 눌러 시작하세요' 에서 멈췄다 —
    `audio.play()` 가 조용히 거절되어 `onPlay` 가 오지 않았고, 그래서 진행 막대도
    움직이지 않았다.

    주소를 없애면 요소가 자원을 다시 읽어야 할 때 길이 끊긴다. 버퍼가 다 찼다고
    해서 안전한 것이 아니다. **재생이 우선이므로 폐기는 화면을 떠날 때만 한다.**

    ※ 그 경로를 막고 싶으면 재생이 끝난 뒤(`onEnded`)에 폐기하는 방법이 남아 있다.
      다만 개발자도구 Network 탭에서 응답을 저장하는 길은 어차피 남는다.
  */

  // ── 음원 받아오기 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!campaignSeq || !userSeq) return;

    // 같은 티켓으로는 한 번만 받는다. 이미 받아 둔 것이 있으면 그대로 쓴다.
    const key = `${campaignSeq}:${ticket ?? ''}`;
    if (loadedRef.current === key) return;
    loadedRef.current = key;

    void (async () => {
      try {
        const blob = await fetchCampaignAudio(userSeq, campaignSeq, sessionid, ticket);

        // 앞서 만들어 둔 주소가 있으면 놓아 준다(티켓이 바뀌어 다시 받은 경우).
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);

        urlRef.current = URL.createObjectURL(blob);
        setSrc(urlRef.current);
      } catch (e) {
        // 다음에 다시 시도할 수 있게 표식을 지운다.
        loadedRef.current = null;

        const message = e instanceof Error ? e.message : MSG.apiFail;
        setFailed(message);
        handlers.current?.onError?.(message);
      }
    })();
  }, [campaignSeq, userSeq, sessionid, ticket]);

  /*
    Blob 주소는 **화면을 떠날 때만** 놓아 준다.

    이펙트 정리(cleanup)에서 놓아 주면, 이펙트가 한 번 더 도는 순간 재생 중이던
    소리가 끊긴다. 주소를 없애면 요소가 자원을 다시 읽어야 할 때 길이 끊기기 때문이다.
  */
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    },
    []
  );

  function toggle() {
    const audio = audioRef.current;
    if (!audio || !src) return;

    if (audio.paused) void audio.play().catch(() => undefined);
    else audio.pause();
  }

  return (
    <div class="audio-player">
      <div class="audio-cover">
        <SafeImage
          src={cover}
          alt=""
          fallback={<span class="audio-cover-empty">♪</span>}
        />

        <button
          type="button"
          class="audio-toggle"
          onClick={toggle}
          disabled={!src}
          aria-label={playing ? '일시정지' : '재생'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
      </div>

      {!src && !failed && <p class="audio-status">음원을 불러오는 중입니다…</p>}
      {failed && <p class="audio-status audio-status--error">{failed}</p>}

      {/*
        controlsList / onContextMenu 로 내려받기 메뉴를 지운다. 기본 컨트롤을
        띄우지 않으니 사실상 필요 없지만, 나중에 controls 를 켜도 새지 않게 둔다.
      */}
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        controlsList="nodownload noplaybackrate"
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={() => {
          const audio = audioRef.current;
          if (!audio) return;
          handlers.current?.onReady?.({
            getCurrentTime: () => audio.currentTime,
            getDuration: () => audio.duration || 0,
            pause: () => audio.pause(),
          });
        }}
        onPlay={() => {
          setPlaying(true);
          handlers.current?.onStateChange?.(1);
        }}
        onPause={() => {
          setPlaying(false);
          // 끝나서 멈춘 것은 onEnded 가 따로 알린다. 여기서 또 보내면 중복이다.
          if (!audioRef.current?.ended) handlers.current?.onStateChange?.(2);
        }}
        onEnded={() => {
          setPlaying(false);
          handlers.current?.onStateChange?.(0);
        }}
        onError={() => {
          const message = '음원을 재생할 수 없습니다.';
          setFailed(message);
          handlers.current?.onError?.(message);
        }}
      />
    </div>
  );
};
